import type { FormEvent, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { InviteLinkForm } from './components/InviteLinkForm';
import type { AppView } from './components/Ui';
import { inviteTokenFromLocation, inviteTokenFromText } from './lib/inviteLink';
import { runDemoRpc, type DemoSnapshot } from './lib/demoMode';
import { isConfigured, isDemoMode, supabase } from './lib/supabase';
import type { WorkspacePanel } from './lib/workspaceBehavior';
import { Workspace } from './Workspace';
import { visualSetlists, visualState } from './visualFixture';

type Profile = { display_name: string; instrument_role: string; phone: string | null };
type Setlist = {
  id: string;
  title: string;
  group_name: string;
  created_at?: string;
  updated_at: string;
  proposal_ids?: string[];
};
type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

export function App() {
  if (isDemoMode) return <DemoRuntime />;
  const visual = import.meta.env.DEV
    ? new URLSearchParams(window.location.search).get('visual')
    : null;
  if (visual) return <VisualPreview screen={visual} />;
  return <AppRuntime />;
}

function DemoRuntime() {
  const [snapshot, setSnapshot] = useState<DemoSnapshot>(() => ({
    profile: {
      display_name: 'Giacomo',
      instrument_role: 'Basso',
      phone: '+393331234567',
    },
    setlists: structuredClone(visualSetlists),
    state: structuredClone(visualState),
  }));
  const snapshotRef = useRef(snapshot);
  return (
    <Workspace
      profile={snapshot.profile}
      setlists={snapshot.setlists}
      visualState={snapshot.state}
      demoMode
      demoRpc={async (name, args) => {
        const result = runDemoRpc(snapshotRef.current, name, args);
        if (!result.error) {
          snapshotRef.current = result.snapshot;
          setSnapshot(result.snapshot);
        }
        return { data: result.data, error: result.error };
      }}
      onRefresh={async () => {}}
      onLogout={async () => {}}
    />
  );
}

function VisualPreview({ screen }: { screen: string }) {
  if (screen === 'invite')
    return (
      <PublicShell>
        <InviteLinkForm token={'a'.repeat(64)} onAccepted={async () => {}} />
      </PublicShell>
    );
  const view: AppView =
    screen === 'map'
      ? 'map'
      : screen === 'setlists'
        ? 'setlists'
        : screen === 'setlist' || screen === 'actions' || screen === 'members'
          ? 'setlist'
          : screen === 'inbox'
            ? 'inbox'
            : screen === 'library' || screen === 'song-form'
              ? 'library'
              : 'home';
  const panel: WorkspacePanel =
    screen === 'profile'
      ? 'profile'
      : screen === 'song-form'
        ? 'song-form'
        : screen === 'actions'
          ? 'actions'
          : screen === 'members'
            ? 'collaborators'
            : null;
  return (
    <Workspace
      profile={{
        display_name: 'Giacomo',
        instrument_role: 'Basso',
        phone: '+393331234567',
      }}
      setlists={visualSetlists}
      visualState={visualState}
      visualView={view}
      visualPanel={panel}
      onRefresh={async () => {}}
      onLogout={async () => {}}
    />
  );
}

function AppRuntime() {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [setlists, setSetlists] = useState<Setlist[]>([]);
  const [busy, setBusy] = useState(true);
  const [profileReady, setProfileReady] = useState(false);
  const [message, setMessage] = useState('');
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const inviteToken = inviteTokenFromLocation(window.location);
  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_my_bootstrap');
    if (error) {
      setMessage(error.message);
      setProfileReady(true);
      return;
    }
    const value = data as { profile: Profile | null; setlists: Setlist[] };
    setProfile(value.profile);
    setSetlists(value.setlists ?? []);
    setMessage('');
    setProfileReady(true);
  }, []);
  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setProfileReady(!data.session);
      setBusy(false);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setProfileReady(!next);
    });
    return () => data.subscription.unsubscribe();
  }, []);
  useEffect(() => {
    if (session) void load();
    else {
      setProfile(null);
      setSetlists([]);
    }
  }, [session, load]);
  useEffect(() => {
    const capturePrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    const installed = () => {
      setInstallPrompt(null);
      setShowInstallGuide(false);
    };
    window.addEventListener('beforeinstallprompt', capturePrompt);
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', capturePrompt);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);
  if (busy || (session && !profileReady))
    return (
      <PublicShell>
        <p>Apertura…</p>
      </PublicShell>
    );
  if (!isConfigured)
    return (
      <PublicShell>
        <Notice>La configurazione locale di Supabase non è disponibile.</Notice>
      </PublicShell>
    );
  if (!session || !profile)
    return (
      <PublicShell>
        {inviteToken ? (
          <InviteLinkForm
            token={inviteToken}
            onAccepted={async () => {
              await load();
              window.history.replaceState({}, '', window.location.pathname);
              setShowInstallGuide(true);
            }}
          />
        ) : (
          <InviteRecoveryForm />
        )}
        {message && <Notice>{message}</Notice>}
      </PublicShell>
    );
  if (showInstallGuide)
    return (
      <InstallGuide
        installPrompt={installPrompt}
        onContinue={() => setShowInstallGuide(false)}
      />
    );
  return (
    <Workspace
      profile={profile}
      setlists={setlists}
      onRefresh={load}
      onLogout={async () => {
        await supabase.auth.signOut({ scope: 'local' });
        setProfile(null);
        setSetlists([]);
      }}
    />
  );
}
function InstallGuide({
  installPrompt,
  onContinue,
}: {
  installPrompt: InstallPromptEvent | null;
  onContinue: () => void;
}) {
  const [installing, setInstalling] = useState(false);
  async function install() {
    if (!installPrompt) return;
    setInstalling(true);
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    setInstalling(false);
    if (choice.outcome === 'accepted') onContinue();
  }
  return (
    <PublicShell>
      <div className="empty install-guide">
        <p className="eyebrow">ACCESSO COMPLETATO</p>
        <h2>Installa Jammo su questo dispositivo</h2>
        <p>
          Installa l’app adesso, dallo stesso browser con cui hai accettato l’invito. In
          questo modo Jammo ritroverà automaticamente il tuo accesso.
        </p>
        {installPrompt ? (
          <button type="button" onClick={() => void install()} disabled={installing}>
            {installing ? 'Installazione…' : 'Installa Jammo'}
          </button>
        ) : (
          <p className="notice">
            Apri il menu del browser e scegli “Installa app” oppure “Aggiungi alla
            schermata Home”.
          </p>
        )}
        <button type="button" className="secondary" onClick={onContinue}>
          Continua nel browser
        </button>
      </div>
    </PublicShell>
  );
}
function InviteRecoveryForm() {
  const [inviteLink, setInviteLink] = useState('');
  const [error, setError] = useState('');
  function submit(event: FormEvent) {
    event.preventDefault();
    const token = inviteTokenFromText(inviteLink);
    if (!token) {
      setError('Incolla il link personale completo ricevuto per Jammo.');
      return;
    }
    window.location.assign(`/invito/rientro?token=${encodeURIComponent(token)}`);
  }
  return (
    <form onSubmit={submit}>
      <h2>Serve un invito personale</h2>
      <p>Se hai già ricevuto il link, incollalo qui per attivare questa app.</p>
      <label>
        Link personale
        <input
          type="url"
          value={inviteLink}
          onChange={(event) => setInviteLink(event.target.value)}
          placeholder="https://scaletta.raislab.it/invito/…"
          required
        />
      </label>
      {error && <Notice>{error}</Notice>}
      <button type="submit">Continua</button>
    </form>
  );
}
function PublicShell({ children }: { children: ReactNode }) {
  return (
    <main className="public-shell">
      <section className="brand">
        <p className="eyebrow">JAMMO</p>
        <h1>Tu che musica sei?</h1>
        <p>Una piccola utility privata tra musicisti.</p>
      </section>
      <section className="auth-card">{children}</section>
    </main>
  );
}
function Notice({ children }: { children: ReactNode }) {
  return (
    <p className="notice" role="status">
      {children}
    </p>
  );
}
